import { Component, Injector, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import SwiperCore, { Navigation } from "swiper";

import { AppClientContext } from 'src/app-client/_appCommon/base/AppClientContext';
import { CRUDFormControllerBase } from 'src/app-client/_appCommon/base/CRUDFormControllerBase';
import { CustomerDataService } from 'src/app-client/_dataservices/CustomerDataService';
import { CustomerLevelDataService } from 'src/app-client/_dataservices/CustomerLevelDataService';
import { ECOMMERCE_USER_TOKEN } from 'src/app/_appCommon/base/Constants';
import { Product, ProductType } from 'src/app-client/_appCommon/base/AppInterfaceAndBaseClasses';
import { ProductDataService } from 'src/app-client/_dataservices/ProductDataService';
import { ShoppingCartDataService } from 'src/app-client/_dataservices/ShoppingCartDataService';
import { SystemServicesDataService } from 'src/app-client/_dataservices/security/SystemServicesDataService';
import { UrlQueryDef } from 'src/app-client/_appCommon/base/UrlQueryDef';
import { GlobalConstants } from 'src/app/_appCommon/base/AppInterfaceAndBaseClasses';
import { IVA } from 'src/app-client/_appCommon/base/Constants';
import { SystemConfigDataService } from 'src/app-client/_dataservices/SystemConfigDataService';

SwiperCore.use([Navigation]);

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.css'],
  providers: [CustomerDataService, CustomerLevelDataService, SystemServicesDataService, SystemConfigDataService],
  encapsulation: ViewEncapsulation.Emulated
})
export class ProductDetailComponent extends CRUDFormControllerBase<ProductDataService>
{
  @ViewChild('confirmDeleteCurrentCart') protected confirmDeleteCurrentCart: any;
  @ViewChild('customInputQuantityExtraAligners') protected customInputQuantityExtraAligners: any;
  @ViewChild('dentalMonitoringDialog') protected dentalMonitoringDialog: any;

  public defaultImage = GlobalConstants.altProductImagePath;

  public addAdditionalAligners: boolean = false;
  public addDentalMonitoring: boolean = true;
  public addIsComplexCase: boolean = false;
  public currentCustomer: any;
  public product: Product;
  public quantityExtraAligners: number = 1;
  public shoppingDiscount: number;
  public isSetupProduct:boolean = false;

  constructor(
    private customerDataService: CustomerDataService,
    private customerLevelDataService: CustomerLevelDataService,
    private injector: Injector,
    private router: Router,
    private shoppingCartDataService: ShoppingCartDataService,
    private systemServicesDataService: SystemServicesDataService,
    protected activatedRoute: ActivatedRoute,
    protected dataService: ProductDataService,
    private systemConfigDataService:SystemConfigDataService
  )
  {
    super(activatedRoute, dataService);
  }

  ngAfterViewInit(): void
  {
    if(AppClientContext.Instance.customerId)
    {
      this.getCustomerInfo();
    }
    else
    {
      this.getStartupInfo();
    }
  }

  ngOnInit(): void
  {
    this.activatedRoute.params.subscribe(params =>
    {
      this.id = +params.id;
      this.verifyToSetDefaultValues(this.id.toString());
    });
  }

  private getPricingInformation(): void
  {
    this.getShoppingDiscount();
  }

  private getProductPrices(): void
  {
    let productPriceListByCustomerId = this.currentEntity.priceList.find(x => x.customerTypeId == this.currentCustomer.customerTypeId);
    let priceProduct = 0;
    let tax = 0;
    let nonTaxable = false;
    let totalTax = 0;
    let subtotal = 0;
    let discount = 0;

    if (productPriceListByCustomerId)
    {
      priceProduct = productPriceListByCustomerId.priceProduct;
      tax = productPriceListByCustomerId.tax;
      nonTaxable = productPriceListByCustomerId.nonTaxable;
      subtotal = nonTaxable || tax != 0 ? priceProduct : priceProduct / (1 + IVA);
      discount = this.shoppingDiscount != 0 ? subtotal * this.shoppingDiscount : 0;
      subtotal -= discount;
      totalTax = subtotal * (nonTaxable? 0 : tax != 0 ? tax : IVA);
      this.product.itemPrices =
      {
        price: priceProduct,
        subtotal: subtotal,
        priceExtraAligner: productPriceListByCustomerId.priceExtraAligner,
        priceExtraDentalMonitoring: productPriceListByCustomerId.priceExtraDentalMonitoring,
        priceComplexCase: productPriceListByCustomerId.priceComplexCase,
        tax: tax,
        nonTaxable: nonTaxable,
        totalTax: totalTax,
        shoppingDiscount: this.shoppingDiscount != 0 ? this.shoppingDiscount : null,
        priceWithDiscount: subtotal + (tax != 0 ? 0 : totalTax),
      };
    }
    else
    {
      priceProduct = this.currentEntity.basePriceProduct;
      tax = this.currentEntity.baseTax;
      nonTaxable = this.currentEntity.nonTaxable;
      subtotal = nonTaxable || tax != 0 ? priceProduct : priceProduct / (1 + IVA);
      discount = this.shoppingDiscount != 0 ? subtotal * this.shoppingDiscount : 0;
      subtotal -= discount;
      totalTax = subtotal * (nonTaxable? 0 : tax != 0 ? tax : IVA);
      this.product.itemPrices =
      {
        price: priceProduct,
        subtotal: subtotal,
        priceExtraAligner: this.currentEntity.basePriceExtraAligner,
        priceExtraDentalMonitoring: this.currentEntity.basePriceDentalMonitoring,
        priceComplexCase: this.currentEntity.basePriceComplexCase,
        tax: tax,
        nonTaxable: nonTaxable,
        totalTax: totalTax,
        shoppingDiscount: this.shoppingDiscount != 0 ? this.shoppingDiscount : null,
        priceWithDiscount: subtotal + (tax != 0 ? 0 : totalTax),
      };
    }
  }

  private getShoppingDiscount(): void
  {
    this.customerLevelDataService.getById(this.currentCustomer.customerLevelId)
      .subscribe((response: any) =>
      {
        this.shoppingDiscount = response.shoppingDiscount ?? 0;
        this.getProductPrices();
      });
  }

  public onAddDentalMonitoringChange(e: any)
  {
    if (!e.checked && e.source.value === this.addDentalMonitoring)
    {
      e.source.checked = true;
      return;
    }

    this.addDentalMonitoring = e.source.value;

    if (!this.addDentalMonitoring)
    {
      this.dentalMonitoringDialog.show = true;
    }
  }

  public onSetAddDentalMonitoring(): void
  {
    this.dentalMonitoringDialog.show = false;
    this.addDentalMonitoring = true;
  }

  public onAddIsComplexCaseChange(e: any)
  {
    if (!e.checked && e.source.value === this.addIsComplexCase)
    {
      e.source.checked = true;
      return;
    }

    this.addIsComplexCase = e.source.value;
  }

  public onClickAddProductToShoppingCart(): void
  {
    if(this.shoppingCartDataService.getCountItems() > 0 && this.shoppingCartDataService.caseId)
    {
      this.confirmDeleteCurrentCart.show = true;
    }
    else
    {
      this.onAddProductToShoppingCart();
    }
  }

  public onAddProductToShoppingCart(): void
  {
    if(this.shoppingCartDataService.caseId)
    {
      this.shoppingCartDataService.onClearItems();
    }

    this.setAdditionalServices();

    let product = JSON.parse(JSON.stringify(this.product));

    this.setInfoAlert(this.shoppingCartDataService.onAddItem(product, 1));
    this.resetValues();

    if(this.shoppingCartDataService.caseId)
    {
      this.router.navigate(['ecommerce/checkout']);
    }
  }

  private setAdditionalServices(): void
  {
    this.product.additionalServices =
    {
      addDentalMonitoring: this.addDentalMonitoring,
      addIsComplexCase: this.addIsComplexCase,
      extraAlignersQuantity: this.addAdditionalAligners ? this.quantityExtraAligners : 0,
      hasUnlimitedAligners: this.product.hasUnlimitedAligners,
    };
  }

  private resetValues(): void
  {
    this.addAdditionalAligners = false;
    if(this.customInputQuantityExtraAligners!=undefined) {this.customInputQuantityExtraAligners.reset();}
    else {this.quantityExtraAligners = 1;}
    
    this.addDentalMonitoring = false;
    this.addIsComplexCase = false;
  }

  verifyToSetDefaultValues(id: string){
    let queryDef = new UrlQueryDef();
    queryDef.params = {
      key : 'DentalMonitoringNotCheckedByDefaultInProducts'
    };

    this.systemConfigDataService.getSystemConfigValueByKey(queryDef).subscribe(res=>{
      let productsWithNoDMChecked: any[] = res.value.split(',');
      if(productsWithNoDMChecked.includes(id)){
        this.addDentalMonitoring = false;
      }
      else{
        this.addDentalMonitoring = true;
      }
    }, err=>{
      this.setDangerAlert(err);
    });
  }

  public get getCaseId(): number
  {
    return this.shoppingCartDataService.caseId;
  }

  public get getCasePatientName(): string
  {
    return this.shoppingCartDataService.casePatientName;
  }

  public isAccesoryProductType(): boolean
  {
    return this.currentEntity?.productType == ProductType.Accessory;
  }

  // ********************************************************************************
  // #region Method Overrides
  // ********************************************************************************
  public getById(id: number, clearAlert: boolean = true): void
  {
    this.dataService.getProductById(this.getQueryDefForGetById())
      .subscribe((response: any) =>
      {
        this.clearAlert(clearAlert);
        this.getByIdCallback(response);
      },
      (fail) =>
      {
        this.router.navigate(['/ecommerce/tienda']);
        this.setDangerAlert(fail.error.errorDetail.message);
      });
  }

  protected onGetQueryDefForGetById(queryDef: UrlQueryDef): void
  {
    queryDef.addParam('id', this.id);
    queryDef.addParam('customerTypeId', this.currentCustomer.customerTypeId);
    super.onGetQueryDefForGetById(queryDef);
  }

  protected onAfterGetById(currentEntity: any)
  {
    this.currentEntity = currentEntity;
    this.product = currentEntity;
    this.isSetupProduct = this.product.productType == ProductType.Setup;
    this.getPricingInformation();
    super.onAfterGetById(currentEntity);
  }

  protected onAfterInsert(currentEntity: any): void
  {

  }
  // #endregion


  // ********************************************************************************
  // #region Methods StartupInfo
  // ********************************************************************************
  private getStartupInfo()
  {
    this.systemServicesDataService.getStartupInfo()
      .subscribe((startupInfo) =>
      {
        this.onApplicationStartup(startupInfo);
      }, error =>
      {
        if (error.status == 401)
        {
          this.forceLogout();
        }
      });
  }

  private onApplicationStartup(startupInfo: any)
  {
    AppClientContext.Instance.initialize(startupInfo);

    // Save a reference to the angular injector to the client context
    AppClientContext.Instance.setInjector(this.injector);

    this.getCustomerInfo();
  }

  private getCustomerInfo() : void
  {
    if (!AppClientContext.Instance.user)
    {
      return;
    }

    this.customerDataService.getByUserId(AppClientContext.Instance.user.id)
      .subscribe((response: any) =>
      {
        this.currentCustomer = response.items.firstOrNull();
        if (this.currentCustomer)
        {
          AppClientContext.Instance.customerId = this.currentCustomer.id;
          super.ngOnInit();
        }
      });
  }

  public forceLogout(): void
  {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');

    // Remove Token
    AppClientContext.Instance.headerValues.delete(ECOMMERCE_USER_TOKEN);

    // Clear current user
    AppClientContext.Instance.user = null;
  }

  private getUserLoginFromCache(): boolean
  {
    let user = JSON.parse(localStorage.getItem('currentUser'));
    return this.setSecurityToken(user);
  }

  private setSecurityToken(user: any): boolean
  {
    if (user && user.cacheToken)
    {
      // Remove Token
      AppClientContext.Instance.headerValues.delete(ECOMMERCE_USER_TOKEN);

      // Append Token
      AppClientContext.Instance.headerValues.set(ECOMMERCE_USER_TOKEN, user.cacheToken);

      return true;
    }
    return false;
  }
  public get getProductTotal(): number
  {
    let itemPrice = this.product?.itemPrices?.subtotal + this.product?.itemPrices.totalTax;
    let dmPrice = this.addDentalMonitoring? this.product?.itemPrices.priceExtraDentalMonitoring : 0;
    let extraAligners = this.addAdditionalAligners? this.product?.itemPrices?.priceExtraAligner * this.quantityExtraAligners : 0;
    let complexCasePrice = this.addIsComplexCase? this.product?.itemPrices?.priceComplexCase : 0;
    return itemPrice + dmPrice + extraAligners + complexCasePrice;
  }
}
